import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const GREEN = '#1a7a4c';
const ORANGE = '#b35c00';

const DIVISION_COLOURS = {
  tech:      { bg: '#e8f0f5', text: '#1a5a7a' },
  hr:        { bg: '#f3eef8', text: '#6b4c8a' },
  finance:   { bg: '#e8f5ef', text: '#1a7a4c' },
  ops:       { bg: '#fff3e6', text: '#b35c00' },
  marketing: { bg: '#fce8e8', text: '#b32d2d' },
};

function divisionStyle(division) {
  return DIVISION_COLOURS[division] || { bg: '#f0f0f0', text: '#555' };
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

function fmtHKD(n) {
  if (n == null || isNaN(n)) return '—';
  return 'HKD ' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function fmtROAS(n) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toFixed(2) + 'x';
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return '—';
  return (Number(n) * 100).toFixed(1) + '%';
}

function MD({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) return <strong key={i}>{p.slice(2, -2)}</strong>;
        if (p.startsWith('*') && p.endsWith('*')) return <em key={i}>{p.slice(1, -1)}</em>;
        return p;
      })}
    </>
  );
}

function TextBlock({ text, style }) {
  if (!text) return null;
  return (
    <div style={style}>
      {String(text).split('\n').map((line, i) => (
        <div key={i} style={{ minHeight: line.trim() ? undefined : '0.6em' }}>
          <MD text={line} />
        </div>
      ))}
    </div>
  );
}

// ── Ad Performance / Budget Review ───────────────────────────────────────────

const PLATFORM_COLOURS = {
  meta:   { bg: '#e8f0fb', text: '#1a4a8a' },
  google: { bg: '#fef9e7', text: '#856404' },
};

const ACTION_COLOURS = {
  increase_budget: { bg: '#e8f5ef', text: '#1a7a4c' },
  decrease_budget: { bg: '#fff3e6', text: '#b35c00' },
  pause:           { bg: '#fce8e8', text: '#b32d2d' },
  scale:           { bg: '#e8f5ef', text: '#1a7a4c' },
  monitor:         { bg: '#f0f0f0', text: '#555' },
  no_action:       { bg: '#f0f0f0', text: '#999' },
};

function platformStyle(p) {
  return PLATFORM_COLOURS[(p || '').toLowerCase()] || { bg: '#f0f0f0', text: '#555' };
}

function actionStyle(a) {
  return ACTION_COLOURS[(a || '').toLowerCase().replace(/ /g, '_')] || { bg: '#f0f0f0', text: '#555' };
}

function Badge({ label, style }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      textTransform: 'uppercase', letterSpacing: '0.04em', ...style,
    }}>
      {label}
    </span>
  );
}

function KpiGrid({ items }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${items.length}, 1fr)`,
      gap: 8, marginBottom: 16,
    }}>
      {items.map(({ label, value, highlight }) => (
        <div key={label} style={{
          background: highlight ? '#fef5f5' : '#f8f8f8',
          borderRadius: 8, padding: '10px 12px',
          borderTop: highlight ? `2px solid ${GARNET}` : '2px solid transparent',
        }}>
          <div style={{ fontSize: 11, color: ASHEN, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            {label}
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: COAL }}>{value}</div>
        </div>
      ))}
    </div>
  );
}

function AdPerformanceDetails({ data }) {
  const [showCampaigns, setShowCampaigns] = useState(false);

  const s = data.summary || data.totals || {};
  const spend = s.total_spend ?? s.spend;
  const revenue = s.total_revenue ?? s.revenue;
  const roas = s.roas ?? (spend && revenue ? revenue / spend : null);
  const conversions = s.total_conversions ?? s.conversions;

  const actions = data.proposed_actions || data.actions || [];
  const campaigns = data.campaigns || [];

  return (
    <div style={{ marginTop: 12 }}>
      <KpiGrid items={[
        { label: 'Spend',    value: fmtHKD(spend) },
        { label: 'Revenue',  value: fmtHKD(revenue) },
        { label: 'ROAS',     value: fmtROAS(roas), highlight: true },
        { label: 'Bookings', value: conversions ?? '—' },
      ]} />

      {actions.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Proposed Actions
          </div>
          {actions.map((a, i) => {
            const ps = platformStyle(a.platform);
            const as = actionStyle(a.action || a.action_type);
            return (
              <div key={i} style={{
                background: '#fff', border: '1px solid #eee', borderRadius: 8,
                padding: '10px 12px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                  <Badge label={a.platform || 'unknown'} style={{ background: ps.bg, color: ps.text }} />
                  <Badge label={(a.action || a.action_type || '').replace(/_/g, ' ')} style={{ background: as.bg, color: as.text }} />
                  {a.campaign && (
                    <span style={{ fontSize: 12, color: COAL, fontWeight: 600, marginLeft: 4 }}>{a.campaign}</span>
                  )}
                </div>
                {a.reason && <div style={{ fontSize: 13, color: '#555' }}>{a.reason}</div>}
                {(a.current_budget != null || a.recommended_spend != null) && (
                  <div style={{ fontSize: 12, color: ASHEN, marginTop: 4 }}>
                    {a.current_budget != null && <>Current: {fmtHKD(a.current_budget)}</>}
                    {a.current_budget != null && a.recommended_spend != null && ' → '}
                    {a.recommended_spend != null && <>Recommended: {fmtHKD(a.recommended_spend)}</>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {campaigns.length > 0 && (
        <div>
          <button
            onClick={() => setShowCampaigns(v => !v)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              fontSize: 12, color: GARNET, fontWeight: 600, marginBottom: 8,
            }}
          >
            {showCampaigns ? '▾ Hide campaigns' : `▸ All campaigns (${campaigns.length})`}
          </button>
          {showCampaigns && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    {['Campaign', 'Platform', 'Spend', 'Revenue', 'ROAS', 'Bookings'].map(h => (
                      <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: ASHEN, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c, i) => {
                    const cr = c.roas ?? (c.spend && c.revenue ? c.revenue / c.spend : null);
                    const rowColor = cr >= 3 ? '#f0faf4' : cr >= 1.5 ? '#fff9f0' : cr != null ? '#fff5f5' : '#fff';
                    return (
                      <tr key={i} style={{ background: rowColor, borderBottom: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '7px 8px', color: COAL, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {c.name || c.campaign_name || '—'}
                        </td>
                        <td style={{ padding: '7px 8px' }}>
                          <Badge label={c.platform || '—'} style={{ ...platformStyle(c.platform), fontSize: 10 }} />
                        </td>
                        <td style={{ padding: '7px 8px', color: COAL }}>{fmtHKD(c.spend)}</td>
                        <td style={{ padding: '7px 8px', color: COAL }}>{fmtHKD(c.revenue)}</td>
                        <td style={{ padding: '7px 8px', fontWeight: 700, color: cr >= 3 ? GREEN : cr >= 1.5 ? ORANGE : '#b32d2d' }}>
                          {fmtROAS(cr)}
                        </td>
                        <td style={{ padding: '7px 8px', color: COAL }}>{c.conversions ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Content Calendar ──────────────────────────────────────────────────────────

function parseContentBrief(brief) {
  if (!brief) return [];
  const parts = brief.split(/---\s*\d+\./);
  return parts
    .map(s => s.trim())
    .filter(Boolean)
    .map(block => {
      const lines = block.split('\n').filter(Boolean);
      const title = lines[0];
      const fields = {};
      let current = null;
      for (const line of lines.slice(1)) {
        const match = line.match(/^\*?\*?([A-Za-z ]+)\*?\*?:\s*(.*)/);
        if (match) {
          current = match[1].trim().toLowerCase().replace(/ /g, '_');
          fields[current] = match[2].trim();
        } else if (current) {
          fields[current] = (fields[current] ? fields[current] + ' ' : '') + line.trim();
        }
      }
      return { title, ...fields };
    });
}

function ContentIdeaCard({ idea, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', background: 'none', border: 'none', padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{
          width: 22, height: 22, borderRadius: '50%', background: GARNET, color: '#fff',
          fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{index + 1}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: COAL, flex: 1 }}>{idea.title || `Idea ${index + 1}`}</span>
        <span style={{ fontSize: 12, color: ASHEN }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 12px 46px' }}>
          {[
            { key: 'content_angle', label: 'Content angle' },
            { key: 'format', label: 'Format' },
            { key: 'hook', label: 'Hook' },
            { key: 'why_it_works', label: 'Why it works' },
          ].map(({ key, label }) => idea[key] ? (
            <div key={key} style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}: </span>
              <span style={{ fontSize: 13, color: COAL }}><MD text={idea[key]} /></span>
            </div>
          ) : null)}
        </div>
      )}
    </div>
  );
}

function ContentCalendarDetails({ data }) {
  const skus = data.top_skus || data.skus || [];
  const brief = data.brief || data.content_brief || '';
  const ideas = parseContentBrief(brief);

  return (
    <div style={{ marginTop: 12 }}>
      {skus.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Top SKUs This Week
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {skus.map((sku, i) => (
              <span key={i} style={{
                background: '#f5f0ff', color: '#5b2d8a', fontSize: 12, fontWeight: 600,
                padding: '4px 10px', borderRadius: 20,
              }}>{sku}</span>
            ))}
          </div>
        </div>
      )}

      {ideas.length > 0 ? (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Content Ideas
          </div>
          {ideas.map((idea, i) => <ContentIdeaCard key={i} idea={idea} index={i} />)}
        </div>
      ) : brief ? (
        <TextBlock text={brief} style={{ fontSize: 13, color: COAL, lineHeight: 1.6 }} />
      ) : null}
    </div>
  );
}

// ── Klaviyo ───────────────────────────────────────────────────────────────────

function KlaviyoDetails({ data }) {
  const campaigns = data.campaigns || [];
  const flows = data.flows || [];
  const lists = data.lists || [];

  if (!campaigns.length && !flows.length && !lists.length) {
    return <div style={{ marginTop: 10, color: ASHEN, fontSize: 13 }}>No email activity this period.</div>;
  }

  const totalRevenue = [...campaigns, ...flows].reduce((sum, r) => sum + (r.revenue || 0), 0);

  return (
    <div style={{ marginTop: 12 }}>
      {totalRevenue > 0 && (
        <div style={{
          background: '#f0faf4', borderRadius: 8, padding: '10px 14px',
          fontSize: 13, fontWeight: 700, color: GREEN, marginBottom: 14, display: 'inline-block',
        }}>
          Email revenue: {fmtHKD(totalRevenue)}
        </div>
      )}

      {campaigns.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Campaigns
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  {['Name', 'Sent', 'Open rate', 'Click rate', 'Revenue'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: ASHEN, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, i) => {
                  const lowOpen = c.open_rate != null && c.open_rate < 0.15;
                  const lowClick = c.click_rate != null && c.click_rate < 0.02;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '7px 8px', color: COAL, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name || c.campaign_name || '—'}
                      </td>
                      <td style={{ padding: '7px 8px', color: COAL }}>{c.sent?.toLocaleString() ?? '—'}</td>
                      <td style={{ padding: '7px 8px', color: lowOpen ? '#b32d2d' : COAL }}>
                        {fmtPct(c.open_rate)}{lowOpen ? ' ⚠' : ''}
                      </td>
                      <td style={{ padding: '7px 8px', color: lowClick ? '#b32d2d' : COAL }}>
                        {fmtPct(c.click_rate)}{lowClick ? ' ⚠' : ''}
                      </td>
                      <td style={{ padding: '7px 8px', color: COAL }}>{c.revenue ? fmtHKD(c.revenue) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {flows.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Flows
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #eee' }}>
                  {['Name', 'Triggered', 'Revenue'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: ASHEN, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 11 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flows.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '7px 8px', color: COAL }}>{f.name || '—'}</td>
                    <td style={{ padding: '7px 8px', color: COAL }}>{f.triggered?.toLocaleString() ?? '—'}</td>
                    <td style={{ padding: '7px 8px', color: COAL }}>{f.revenue ? fmtHKD(f.revenue) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {lists.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ASHEN, marginBottom: 8 }}>
            Lists
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {lists.map((l, i) => (
              <span key={i} style={{
                background: '#e8f0fb', color: '#1a4a8a', fontSize: 12, fontWeight: 600,
                padding: '4px 10px', borderRadius: 20,
              }}>
                {typeof l === 'string' ? l : l.name || JSON.stringify(l)}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Raw JSON fallback ─────────────────────────────────────────────────────────

function DataViewer({ data }) {
  const [open, setOpen] = useState(false);
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <div style={{ marginTop: '0.5rem' }}>
      <span
        style={{ fontSize: '0.75rem', color: '#5b1e22', cursor: 'pointer', fontWeight: 600 }}
        onClick={() => setOpen(o => !o)}
      >
        {open ? '▾ Hide details' : '▸ Show details'}
      </span>
      {open && (
        <pre style={{
          marginTop: '0.5rem', background: '#f7f7f7', borderRadius: 8,
          padding: '0.75rem', fontSize: '0.75rem', overflowX: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333',
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function DetailsRenderer({ action_type, data }) {
  if (!data) return null;
  const t = (action_type || '').toLowerCase();
  if (t === 'ad_performance_report' || t === 'ad_budget_review') return <AdPerformanceDetails data={data} />;
  if (t === 'content_calendar_brief') return <ContentCalendarDetails data={data} />;
  if (t === 'klaviyo_weekly_report') return <KlaviyoDetails data={data} />;
  return <DataViewer data={data} />;
}

// ── Card & page ───────────────────────────────────────────────────────────────

function truncateFirstLine(text) {
  if (!text) return '';
  const first = String(text).split('\n')[0];
  return first.length > 120 ? first.slice(0, 117) + '…' : first;
}

function ApprovalCard({ item, onAction }) {
  const [acting, setActing] = useState(null);
  const ds = divisionStyle(item.division);

  const handleAction = async (action) => {
    setActing(action);
    try {
      await api.post(`/approvals/${item.id}/${action}`);
      onAction(item.id, action);
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed');
    } finally {
      setActing(null);
    }
  };

  const isCalendar = (item.action_type || '').toLowerCase() === 'content_calendar_brief';
  const displayDesc = isCalendar ? truncateFirstLine(item.description) : item.description;

  return (
    <div className="card" style={{ marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.6rem',
              borderRadius: 20, background: ds.bg, color: ds.text, textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>
              {item.division}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#999', fontFamily: 'monospace' }}>
              {item.action_type}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#bbb' }}>
              {formatDateTime(item.created_at)}
            </span>
          </div>
          <div style={{ fontSize: '0.95rem', fontWeight: 500, color: '#181617' }}>
            {displayDesc}
          </div>
          <DetailsRenderer action_type={item.action_type} data={item.data} />
        </div>

        {item.status === 'pending' && (
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
            <button
              className="btn btn-primary btn-sm"
              disabled={!!acting}
              onClick={() => handleAction('approve')}
              style={{ minWidth: 80 }}
            >
              {acting === 'approve' ? '...' : '✓ Approve'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              disabled={!!acting}
              onClick={() => handleAction('reject')}
              style={{ minWidth: 80, color: '#b32d2d', borderColor: '#b32d2d' }}
            >
              {acting === 'reject' ? '...' : '✗ Reject'}
            </button>
          </div>
        )}

        {item.status !== 'pending' && (
          <span style={{
            fontSize: '0.8rem', fontWeight: 600, padding: '0.25rem 0.75rem',
            borderRadius: 20, flexShrink: 0,
            background: item.status === 'approved' ? '#e8f5ef' : '#fce8e8',
            color: item.status === 'approved' ? '#1a7a4c' : '#b32d2d',
          }}>
            {item.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const res = await api.get('/approvals', { params: { status } });
      setItems(res.data.approvals);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const handleAction = (id) => {
    if (tab === 'pending') setItems(prev => prev.filter(i => i.id !== id));
  };

  const pending = items.filter(i => i.status === 'pending').length;

  return (
    <div>
      <div className="page-header">
        <h1>
          Agent Approvals{pending > 0 && tab === 'pending' && (
            <span style={{ fontSize: '1rem', color: '#5b1e22', fontWeight: 600 }}> ({pending})</span>
          )}
        </h1>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={tab === s ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            style={{ textTransform: 'capitalize' }}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '2.5rem', color: '#999' }}>
          {tab === 'pending' ? 'No pending approvals — agents are quiet.' : `No ${tab} approvals.`}
        </div>
      ) : (
        items.map(item => (
          <ApprovalCard key={item.id} item={item} onAction={handleAction} />
        ))
      )}
    </div>
  );
}
